import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, Plus, Maximize2, Minimize2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import CurrencyInput from '../../components/Admin/CurrencyInput';
import { Cambio, Moeda, formatMoeda, labelCambio } from '../../utils/orcamentoPagamento';
import { Button, IconButton, Tabs } from '../../components/UI';

interface ClienteOption {
  _id: string;
  nome: string;
}

interface ParcelaPaga {
  _id: string;
  numeroParcela: number;
  totalParcelas: number;
  valor: number;
  dataPagamento?: string | null;
}

interface ParcelaPersonalizada {
  valor: string;
  vencimento: string;
}

interface VendaFormData {
  clienteId: string;
  descricao: string;
  valorTotal: string;
  formaPagamento: 'avista' | 'parcelado_2' | 'parcelado_3' | 'personalizado';
  tipoServico: string;
  status: 'aberta' | 'concluida' | 'cancelada';
}

const FORMA_PAGAMENTO_PREVIEW: { [key: string]: string } = {
  avista: 'Pagamento único no ato da venda.',
  parcelado_2: 'Gera 2 parcelas: a primeira no ato da venda e a segunda 30 dias depois.',
  parcelado_3: 'Gera 3 parcelas: a primeira no ato da venda, a segunda em 30 dias e a terceira em 60 dias.',
  personalizado: 'Defina você mesmo as datas e valores de cada parcela.'
};

const hojeISO = () => new Date().toISOString().slice(0, 10);
const round2 = (n: number) => Math.round(n * 100) / 100;

const VendaForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const clienteIdFromQuery = searchParams.get('clienteId');
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();
  const { isSuperAdmin } = useAuth();
  const confirm = useConfirm();

  const [formData, setFormData] = useState<VendaFormData>({
    clienteId: clienteIdFromQuery || '',
    descricao: '',
    valorTotal: '',
    formaPagamento: 'parcelado_3',
    tipoServico: '',
    status: 'aberta'
  });
  // Moeda da venda; em BRL a cotação fica congelada na venda e é copiada para as parcelas
  const [moeda, setMoeda] = useState<Moeda>('EUR');
  const [cambio, setCambio] = useState<Cambio | null>(null);
  const [carregandoCambio, setCarregandoCambio] = useState(false);
  const simbolo = moeda === 'BRL' ? 'R$' : '€';
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [clienteFixo, setClienteFixo] = useState<{ _id: string; nome: string } | null>(null);
  const [parcelasPagasExistentes, setParcelasPagasExistentes] = useState<ParcelaPaga[]>([]);
  const [loading, setLoading] = useState(false);
  const [regenerando, setRegenerando] = useState(false);
  const [showForcarConfirm, setShowForcarConfirm] = useState(false);
  const { isExpanded: isForcarModalExpanded, setIsExpanded: setIsForcarModalExpanded, toggleExpanded: toggleForcarModalExpanded } = useExpandable();
  useEscapeKey(() => { setShowForcarConfirm(false); setIsForcarModalExpanded(false); }, showForcarConfirm);
  const [parcelasPersonalizadas, setParcelasPersonalizadas] = useState<ParcelaPersonalizada[]>([
    { valor: '', vencimento: hojeISO() }
  ]);
  const [condicoesOriginais, setCondicoesOriginais] = useState<{
    valorTotal: number;
    formaPagamento: string;
    parcelasPersonalizadas: ParcelaPersonalizada[];
    moeda: Moeda;
  } | null>(null);

  const fetchClientes = useCallback(async () => {
    try {
      const response = await axios.get('/clientes?limit=200');
      setClientes(response.data.clientes);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  }, []);

  const fetchVenda = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/vendas/${id}`);
      const venda = response.data.venda;
      setFormData({
        clienteId: venda.clienteId?._id || '',
        descricao: venda.descricao || '',
        valorTotal: String(venda.valorTotal ?? ''),
        formaPagamento: venda.formaPagamento || 'parcelado_3',
        tipoServico: venda.tipoServico || '',
        status: venda.status || 'aberta'
      });
      setClienteFixo(venda.clienteId || null);
      const moedaVenda: Moeda = venda.moeda === 'BRL' ? 'BRL' : 'EUR';
      setMoeda(moedaVenda);
      setCambio(moedaVenda === 'BRL' ? venda.cambio || null : null);
      const pagamentos = response.data.pagamentos || [];
      setParcelasPagasExistentes(
        pagamentos
          .filter((p: any) => p.status === 'pago')
          .map((p: any) => ({
            _id: p._id,
            numeroParcela: p.numeroParcela,
            totalParcelas: p.totalParcelas,
            valor: p.valor,
            dataPagamento: p.dataPagamento
          }))
      );
      const parcelasIniciais = venda.formaPagamento === 'personalizado' && pagamentos.length > 0
        ? pagamentos.map((p: any) => ({
            valor: String(p.valor),
            vencimento: p.vencimento ? p.vencimento.slice(0, 10) : hojeISO()
          }))
        : [{ valor: '', vencimento: hojeISO() }];
      setParcelasPersonalizadas(parcelasIniciais);
      setCondicoesOriginais({
        valorTotal: venda.valorTotal,
        formaPagamento: venda.formaPagamento,
        parcelasPersonalizadas: parcelasIniciais,
        moeda: moedaVenda
      });
    } catch (error) {
      console.error('Erro ao carregar venda:', error);
    }
  }, [id]);

  const fetchClienteFixo = useCallback(async () => {
    if (!clienteIdFromQuery) return;
    try {
      const response = await axios.get(`/clientes/${clienteIdFromQuery}`);
      setClienteFixo({ _id: response.data.cliente._id, nome: response.data.cliente.nome });
    } catch (error) {
      console.error('Erro ao carregar cliente:', error);
    }
  }, [clienteIdFromQuery]);

  useEffect(() => {
    if (clienteIdFromQuery) {
      fetchClienteFixo();
    } else {
      fetchClientes();
    }
  }, [clienteIdFromQuery, fetchClientes, fetchClienteFixo]);

  useEffect(() => {
    if (isEditing && id) {
      fetchVenda();
    }
  }, [isEditing, id, fetchVenda]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.clienteId) {
      notifications.warning.validation('Selecione um cliente.');
      setLoading(false);
      return;
    }
    if (!formData.descricao.trim()) {
      notifications.warning.validation('Informe a descrição da venda.');
      setLoading(false);
      return;
    }
    if (!formData.valorTotal || Number(formData.valorTotal) <= 0) {
      notifications.warning.validation('Informe um valor total válido.');
      setLoading(false);
      return;
    }

    if (formData.formaPagamento === 'personalizado') {
      if (parcelasPersonalizadas.some((p) => !p.vencimento || !p.valor || Number(p.valor) <= 0)) {
        notifications.warning.validation('Preencha data e valor de todas as parcelas personalizadas.');
        setLoading(false);
        return;
      }
      const somaParcelas = parcelasPersonalizadas.reduce((acc, p) => acc + Number(p.valor), 0);
      if (Math.abs(somaParcelas - Number(formData.valorTotal)) > 0.01) {
        notifications.warning.validation(
          `A soma das parcelas (${formatMoeda(somaParcelas, moeda)}) deve ser igual ao valor total (${formatMoeda(Number(formData.valorTotal), moeda)}).`
        );
        setLoading(false);
        return;
      }
    }

    const mudouCondicoes = Boolean(
      isEditing &&
      condicoesOriginais &&
      (
        moeda !== condicoesOriginais.moeda ||
        Number(formData.valorTotal) !== condicoesOriginais.valorTotal ||
        formData.formaPagamento !== condicoesOriginais.formaPagamento ||
        (formData.formaPagamento === 'personalizado' &&
          JSON.stringify(parcelasPersonalizadas) !== JSON.stringify(condicoesOriginais.parcelasPersonalizadas))
      )
    );

    if (mudouCondicoes && parcelasPagasExistentes.length > 0) {
      setLoading(false);
      setShowForcarConfirm(true);
      return;
    }

    const payload = {
      ...formData,
      valorTotal: Number(formData.valorTotal),
      moeda,
      cambio: moeda === 'BRL' ? cambio : null,
      ...(formData.formaPagamento === 'personalizado' ? { parcelasPersonalizadas } : {})
    };

    try {
      if (isEditing) {
        await axios.put(`/vendas/${id}`, payload);
        notifications.success.vendaUpdated();
      } else {
        await axios.post('/vendas', payload);
        notifications.success.vendaCreated();
      }
      setTimeout(() => navigate(formData.clienteId ? `/admin/clientes/${formData.clienteId}` : '/admin/crm/vendas'), 1000);
    } catch (error: any) {
      console.error('Erro ao salvar venda:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        notifications.error.vendaCreate(errorMessages);
      } else {
        notifications.error.vendaCreate(error.response?.data?.message || 'Erro ao salvar venda');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForcarRegenerar = async () => {
    setRegenerando(true);
    try {
      await axios.put(`/vendas/${id}/parcelas/regenerar`, {
        force: true,
        valorTotal: Number(formData.valorTotal),
        formaPagamento: formData.formaPagamento,
        ...(formData.formaPagamento === 'personalizado' ? { parcelasPersonalizadas } : {})
      });
      notifications.success.vendaRegenerada();
      setShowForcarConfirm(false);
      setIsForcarModalExpanded(false);
      setTimeout(() => navigate(formData.clienteId ? `/admin/clientes/${formData.clienteId}` : '/admin/crm/vendas'), 1000);
    } catch (error: any) {
      console.error('Erro ao forçar regeneração de parcelas:', error);
      notifications.error.vendaUpdate(
        error.response?.status === 403
          ? 'Apenas super administradores podem forçar a regeneração de parcelas com pagamentos já registrados.'
          : (error.response?.data?.message || 'Erro ao forçar regeneração de parcelas')
      );
    } finally {
      setRegenerando(false);
    }
  };

  const fetchCambio = async (forcar = false): Promise<Cambio | null> => {
    try {
      setCarregandoCambio(true);
      const res = await axios.get(`/cambio?from=EUR&to=BRL${forcar ? '&atualizar=1' : ''}`);
      const { taxa, fonte, obtidaEm, desatualizada } = res.data;
      return { taxa, fonte, obtidaEm, desatualizada };
    } catch (error: any) {
      notifications.error.settingsUpdate(error.response?.data?.message || 'Não foi possível obter a cotação EUR/BRL');
      return null;
    } finally {
      setCarregandoCambio(false);
    }
  };

  // Venda existente mantém a cotação gravada; só busca outra ao criar ou ao trocar de moeda
  const moedaTravada = parcelasPagasExistentes.length > 0;
  const podeAtualizarCambio = moeda === 'BRL' && (!isEditing || moeda !== condicoesOriginais?.moeda);

  const handleMoedaChange = async (nova: Moeda) => {
    if (nova === moeda || moedaTravada) return;
    const voltandoAoOriginal = isEditing && nova === condicoesOriginais?.moeda;
    let novoCambio = cambio;
    if (nova === 'BRL') {
      novoCambio = voltandoAoOriginal && cambio ? cambio : await fetchCambio();
      if (!novoCambio) return;
    }
    const temValores = Number(formData.valorTotal) > 0 || parcelasPersonalizadas.some((p) => Number(p.valor) > 0);
    let converter = false;
    if (temValores && novoCambio?.taxa) {
      converter = await confirm({
        title: 'Converter valores',
        message: `Converter os valores já preenchidos para ${nova === 'BRL' ? 'reais' : 'euros'} pela cotação ${labelCambio(novoCambio)}? Se não, os números ficam como estão.`,
        confirmText: 'Converter',
        cancelText: 'Manter números'
      });
    }
    if (converter && novoCambio) {
      const fator = nova === 'BRL' ? novoCambio.taxa : 1 / novoCambio.taxa;
      const converterValor = (v: string) => (Number(v) > 0 ? String(round2(Number(v) * fator)) : v);
      setFormData((prev) => ({ ...prev, valorTotal: converterValor(prev.valorTotal) }));
      setParcelasPersonalizadas((prev) => prev.map((p) => ({ ...p, valor: converterValor(p.valor) })));
    }
    setMoeda(nova);
    // Em EUR a cotação fica guardada só para converter de volta, se o admin voltar para R$
    if (novoCambio) setCambio(novoCambio);
  };

  const atualizarCambio = async () => {
    const novo = await fetchCambio(true);
    if (novo) setCambio(novo);
  };

  const somaParcelasPersonalizadas = parcelasPersonalizadas.reduce((acc, p) => acc + (Number(p.valor) || 0), 0);
  const diferencaParcelas = Number(formData.valorTotal || 0) - somaParcelasPersonalizadas;

  const handleParcelaChange = (index: number, field: keyof ParcelaPersonalizada, value: string) => {
    setParcelasPersonalizadas((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleAddParcela = () => {
    setParcelasPersonalizadas((prev) => [...prev, { valor: '', vencimento: hojeISO() }]);
  };

  const handleRemoveParcela = (index: number) => {
    setParcelasPersonalizadas((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="mb-4 px-4 sm:px-0">
          <Breadcrumbs />
        </div>
        <div className="mb-8 px-4 sm:px-0">
          <div className="flex items-center space-x-4">
            <IconButton action="back" onClick={() => navigate(-1)} />
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Editar venda' : 'Nova venda'}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 px-4 sm:px-0">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
              {clienteFixo || clienteIdFromQuery ? (
                <input
                  type="text"
                  value={clienteFixo?.nome || 'Cliente selecionado'}
                  disabled
                  className="field bg-gray-100 text-gray-500"
                />
              ) : (
                <select
                  value={formData.clienteId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, clienteId: e.target.value }))}
                  className="field field--select"
                  required
                >
                  <option value="">Selecione um cliente</option>
                  {clientes.map((cliente) => (
                    <option key={cliente._id} value={cliente._id}>{cliente.nome}</option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <input
                type="text"
                value={formData.descricao}
                onChange={(e) => setFormData((prev) => ({ ...prev, descricao: e.target.value }))}
                placeholder="Ex: Assessoria de cidadania portuguesa"
                className="field"
                required
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg bg-gray-50">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Moeda da venda</label>
                <Tabs
                  variant="segmented"
                  ariaLabel="Moeda"
                  value={moeda}
                  onChange={(m) => handleMoedaChange(m)}
                  items={(['EUR', 'BRL'] as Moeda[]).map((m) => ({ id: m, label: m === 'EUR' ? '€ Euro' : 'R$ Real', disabled: carregandoCambio || moedaTravada }))}
                />
              </div>
              <div className="text-sm text-gray-600 sm:ml-2">
                {moeda === 'BRL' && (
                  cambio ? (
                    <p className={cambio.desatualizada ? 'text-amber-700' : ''}>
                      {labelCambio(cambio)}
                      {cambio.desatualizada && ' (fonte indisponível — última cotação conhecida)'}
                    </p>
                  ) : (
                    <p>Cotação ainda não carregada.</p>
                  )
                )}
                {podeAtualizarCambio && (
                  <button
                    type="button"
                    onClick={atualizarCambio}
                    disabled={carregandoCambio}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${carregandoCambio ? 'animate-spin' : ''}`} /> Atualizar cotação
                  </button>
                )}
                {moedaTravada ? (
                  <p className="text-xs text-gray-500 mt-1">A moeda não pode ser trocada: a venda já tem parcela paga.</p>
                ) : moeda === 'BRL' ? (
                  <p className="text-xs text-gray-500 mt-1">
                    A cotação fica gravada na venda e nas parcelas; os relatórios convertem para € por ela.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor total ({simbolo})</label>
                <CurrencyInput
                  moeda={moeda}
                  value={Number(formData.valorTotal) || 0}
                  onChange={(v) => setFormData((prev) => ({ ...prev, valorTotal: String(v) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de serviço</label>
                <input
                  type="text"
                  value={formData.tipoServico}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tipoServico: e.target.value }))}
                  className="field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Forma de pagamento</label>
              <select
                value={formData.formaPagamento}
                onChange={(e) => setFormData((prev) => ({ ...prev, formaPagamento: e.target.value as VendaFormData['formaPagamento'] }))}
                className="field field--select"
              >
                <option value="avista">À vista</option>
                <option value="parcelado_2">Parcelado em 2x</option>
                <option value="parcelado_3">Parcelado em 3x</option>
                <option value="personalizado">Personalizado</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">{FORMA_PAGAMENTO_PREVIEW[formData.formaPagamento]}</p>
              {parcelasPagasExistentes.length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Esta venda já tem {parcelasPagasExistentes.length} parcela(s) paga(s). Alterar valor/forma de pagamento
                  exigirá forçar a regeneração de todas as parcelas{!isSuperAdmin && ' (apenas super administradores podem fazer isso)'}.
                </p>
              )}
            </div>

            {formData.formaPagamento === 'personalizado' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Parcelas personalizadas</label>
                <div className="space-y-2">
                  {parcelasPersonalizadas.map((parcela, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="date"
                        value={parcela.vencimento}
                        onChange={(e) => handleParcelaChange(index, 'vencimento', e.target.value)}
                        className="field flex-1"
                        required
                      />
                      <CurrencyInput
                        aria-label={`Valor (${simbolo})`}
                        moeda={moeda}
                        value={Number(parcela.valor) || 0}
                        onChange={(v) => handleParcelaChange(index, 'valor', String(v))}
                        className="w-36 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                      <IconButton action="delete" label="Remover parcela" onClick={() => handleRemoveParcela(index)} disabled={parcelasPersonalizadas.length === 1} className="disabled:opacity-50" />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAddParcela}
                  className="mt-2 flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-800"
                >
                  <Plus className="w-4 h-4" />
                  <span>Adicionar parcela</span>
                </button>
                <p className={`text-xs mt-2 ${Math.abs(diferencaParcelas) > 0.01 ? 'text-red-600' : 'text-gray-500'}`}>
                  Total das parcelas: {formatMoeda(somaParcelasPersonalizadas, moeda)}
                  {Math.abs(diferencaParcelas) > 0.01 &&
                    ` (${diferencaParcelas > 0 ? 'faltam' : 'excedem'} ${formatMoeda(Math.abs(diferencaParcelas), moeda)})`}
                </p>
              </div>
            )}

            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as VendaFormData['status'] }))}
                  className="field field--select"
                >
                  <option value="aberta">Aberta</option>
                  <option value="concluida">Concluída</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
            )}
          </div>

          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4" />
              <span>{loading ? t('common.saving') : t('common.save')}</span>
            </Button>
          </div>
        </form>
      </div>

      {showForcarConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto ${
            isForcarModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-md max-h-[90vh]'
          }`}>
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Forçar exclusão e refazer parcelas</h3>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={toggleForcarModalExpanded}
                    className="icon-btn text-gray-400 hover:text-gray-600 transition-colors p-1"
                    title={isForcarModalExpanded ? 'Restaurar' : 'Expandir'}
                  >
                    {isForcarModalExpanded ? <Minimize2 className="w-5 h-5 sm:w-6 sm:h-6" /> : <Maximize2 className="w-5 h-5 sm:w-6 sm:h-6" />}
                  </button>
                  <IconButton action="close" onClick={() => { setShowForcarConfirm(false); setIsForcarModalExpanded(false); }} iconClassName="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                Esta venda já tem {parcelasPagasExistentes.length} parcela(s) paga(s). Alterar o valor total ou a forma
                de pagamento exige excluir <strong>todas</strong> as parcelas atuais e recriá-las do zero — incluindo as
                já pagas, cujo registro de pagamento será perdido. Esta ação é irreversível.
              </p>
              <ul className="text-sm text-gray-600 border border-gray-200 rounded-lg divide-y divide-gray-100">
                {parcelasPagasExistentes.map((p) => (
                  <li key={p._id} className="px-3 py-2 flex justify-between">
                    <span>Parcela {p.numeroParcela}/{p.totalParcelas}</span>
                    <span>{formatMoeda(p.valor, moeda)} — paga em {p.dataPagamento ? new Date(p.dataPagamento).toLocaleDateString('pt-BR') : '—'}</span>
                  </li>
                ))}
              </ul>
              {!isSuperAdmin && (
                <p className="text-xs text-amber-600">
                  Apenas super administradores podem forçar essa regeneração. Peça a um super admin para realizar esta alteração.
                </p>
              )}
            </div>
            <div className="form-actions px-4 sm:px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => { setShowForcarConfirm(false); setIsForcarModalExpanded(false); }}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              {isSuperAdmin && (
                <Button variant="danger" size="sm" className="w-full sm:w-auto"
                  type="button"
                  onClick={handleForcarRegenerar}
                  disabled={regenerando}>
                  {regenerando ? 'Refazendo...' : 'Forçar exclusão e refazer'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendaForm;
